import { getCurrencyByCode } from '@/constants/currencies'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `YYYYMMDD` (date-only) → "MMM D, YYYY", or a dash placeholder when unset/invalid. */
export const formatDateOnly = (yyyymmdd: string | null): string => {
	if (!yyyymmdd || yyyymmdd.length !== 8) return '—'
	const year = yyyymmdd.slice(0, 4)
	const month = Number(yyyymmdd.slice(4, 6)) - 1
	const day = Number(yyyymmdd.slice(6, 8))
	if (month < 0 || month > 11) return '—'
	return `${MONTHS[month]} ${day}, ${year}`
}

/** Unix seconds → "MMM D, YYYY" in the viewer's locale. */
export const formatDate = (unixSeconds: number): string =>
	new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	})

/** Unix seconds → "MMM D, YYYY, h:mm AM/PM" in the viewer's locale. */
export const formatDateTime = (unixSeconds: number): string =>
	new Date(unixSeconds * 1000).toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	})

/**
 * A trip's date span: `startDate` (`YYYYMMDD`) through `startDate + dayCount - 1`.
 * Returns the start alone for 0/1-day trips, or "—" when no start is set.
 */
export const formatTripDates = (startDate: string | null, dayCount: number): string => {
	if (!startDate || startDate.length !== 8) return '—'
	const start = formatDateOnly(startDate)
	if (dayCount <= 1) return start
	const year = Number(startDate.slice(0, 4))
	const month = Number(startDate.slice(4, 6)) - 1
	const day = Number(startDate.slice(6, 8))
	const end = new Date(Date.UTC(year, month, day + (dayCount - 1)))
	const endStr = `${MONTHS[end.getUTCMonth()]} ${end.getUTCDate()}, ${end.getUTCFullYear()}`
	return `${start} – ${endStr}`
}

/** Format minor-unit amounts the same way the consumer expense UI does. */
export const formatAmount = (minorUnits: number, currencyCode: string): string => {
	const currency = getCurrencyByCode(currencyCode)
	const value = (minorUnits / 100).toFixed(2)
	if (!currency) return `${value} ${currencyCode.toUpperCase()}`
	return currency.isSymbolOnLeft ? `${currency.symbol}${value}` : `${value}${currency.symbol}`
}

/** "First Last", trimmed; falls back to a dash when both names are empty. */
export const formatName = (firstName: string, lastName: string): string =>
	`${firstName} ${lastName}`.trim() || '—'
